import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard.server";

const requireAdminCtx = async (context: unknown) => {
  await assertAdmin((context as { userId?: string }).userId);
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

const ArticleInput = z.object({
  id: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(2).max(200),
  slug: z.string().trim().max(80).optional().nullable(),
  excerpt: z.string().trim().max(500).optional().nullable(),
  content: z.string().trim().min(1).max(50000),
  cover_image_url: z.string().trim().max(1000).optional().nullable(),
  category: z.string().trim().max(80).optional().nullable(),
  author: z.string().trim().max(120).optional().nullable(),
  published: z.boolean().optional().default(false),
});

// PUBLIC: list published articles
export const listPublishedArticles = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("articles")
    .select("id,slug,title,excerpt,cover_image_url,category,author,published_at")
    .eq("published", true)
    .order("published_at", { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  return { articles: data ?? [] };
});

// PUBLIC: get single published article by slug
export const getArticleBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ slug: z.string().min(1).max(120) }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("articles")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { article: row };
  });

// ADMIN: list all articles
export const adminListArticles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdminCtx(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("articles")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { articles: data ?? [] };
  });

// ADMIN: upsert article
export const upsertArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => ArticleInput.parse(data))
  .handler(async ({ data, context }) => {
    await requireAdminCtx(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const baseSlug = data.slug?.trim() ? slugify(data.slug) : slugify(data.title);
    let slug = baseSlug || "article";

    // Ensure unique slug
    let suffix = 0;
    while (true) {
      const candidate = suffix === 0 ? slug : `${slug}-${suffix}`;
      const q = supabaseAdmin.from("articles").select("id").eq("slug", candidate);
      const { data: existing } = await q.maybeSingle();
      if (!existing || existing.id === data.id) {
        slug = candidate;
        break;
      }
      suffix += 1;
      if (suffix > 50) throw new Error("Could not generate unique slug");
    }

    const payload = {
      title: data.title,
      slug,
      excerpt: data.excerpt || null,
      content: data.content,
      cover_image_url: data.cover_image_url || null,
      category: data.category || null,
      author: data.author || null,
      published: !!data.published,
      published_at: data.published ? new Date().toISOString() : null,
      created_by: (context as { userId?: string }).userId ?? null,
    };

    if (data.id) {
      const { error } = await supabaseAdmin
        .from("articles")
        .update({
          ...payload,
          // keep original published_at if already published
          published_at: payload.published ? payload.published_at : null,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id, slug };
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("articles")
      .insert(payload)
      .select("id,slug")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted.id, slug: inserted.slug };
  });

// ADMIN: delete article
export const deleteArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await requireAdminCtx(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("articles").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ADMIN: upload an image (base64) -> returns public URL served by /api/public/article-image
export const uploadArticleImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        filename: z.string().min(1).max(200),
        contentType: z.string().min(1).max(100),
        // base64 (no data: prefix)
        base64: z.string().min(1).max(15_000_000),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await requireAdminCtx(context);
    if (!data.contentType.startsWith("image/")) throw new Error("Only images allowed");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const ext = (data.filename.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "");
    const safeExt = ext.slice(0, 6) || "bin";
    const key = `articles/${crypto.randomUUID()}.${safeExt}`;

    const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
    const { error } = await supabaseAdmin.storage
      .from("article-images")
      .upload(key, bytes, { contentType: data.contentType, upsert: false });
    if (error) throw new Error(error.message);

    return { url: `/api/public/article-image/${key}`, path: key };
  });
